<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { usePaginacao } from '@/composables/usePaginacao'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import CampoDataIntervalo from '@/components/base/campos/CampoDataIntervalo.vue'
import { formatarDataBR } from '@/utils/useDataBR'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({
  tiposHedge: [],
  instituicoes: [],
  statusHedge: [],
  moedas: []
})

const filtros = reactive({
  buscaLivre: '',
  lstTipoHedge: '',
  lstStatusHedge: '',
  lstMoedaEstrangeira: '',
  idInstituicaoFinanceira: '',
  dataFechamentoInicio: '',
  dataFechamentoFim: '',
  dataVencimentoInicio: '',
  dataVencimentoFim: ''
})

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const temPermissaoCompleta = computed(() => authStore.pode('HEDGE_COMPLETO'))
const podeVerAcoes = computed(() => temPermissaoCompleta.value || authStore.pode('HEDGE_VISUALIZACAO'))

const colunas = [
  { field: 'idOperacaoHedge', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'numeroContrato', label: 'Contrato', width: 150, sortable: true, class: 'fw-bold' },
  { field: 'nomeInstituicaoFinanceira', label: 'Instituição Financeira', sortable: true },
  { field: 'lstTipoHedge', label: 'Tipo', align: 'center', sortable: true, slotName: 'tipo' },
  { field: 'lstTipoPosicao', label: 'Posição', align: 'center', sortable: true, slotName: 'posicao' },
  { field: 'lstStatusHedge', label: 'Status', align: 'center', sortable: true, slotName: 'status' },
  { field: 'dataFechamento', label: 'Fechamento', align: 'center', sortable: true, slotName: 'fechamento' },
  { field: 'dataVencimento', label: 'Vencimento', align: 'center', sortable: true, slotName: 'vencimento' },
  { field: 'lstMoedaEstrangeira', label: 'Moeda', align: 'center', sortable: true, slotName: 'moeda' },
  { field: 'valorMoedaEstrangeira', label: 'Valor Notional', align: 'right', sortable: true, slotName: 'valorNotional' },
  { field: 'taxaContratada', label: 'Taxa Trava', align: 'right', sortable: true, slotName: 'taxa' },
  { field: 'acoes', label: 'Ações', width: 200, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstTipoHedge: (row) => row.lstTipoHedge || '-',
    lstTipoPosicao: (row) => row.lstTipoPosicao || '-',
    lstStatusHedge: (row) => getTextoStatus(row.lstStatusHedge),
    lstMoedaEstrangeira: (row) => getTextoMoeda(row.lstMoedaEstrangeira)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => { buscarDados(1) }

const carregarListas = async () => {
  try {
    const [resInst, tiposHedge, statusHedge, moedas] = await Promise.all([
      api.get('/Parceiros?lstTipoPessoa=PARCEIRO_TIPO5&PageSize=500'),
      metadataStore.getListaPorCampo('OperacaoHedge', 'lstTipoHedge'),
      metadataStore.getListaPorCampo('OperacaoHedge', 'lstStatusHedge'),
      metadataStore.getListaPorCampo('OperacaoHedge', 'lstMoedaEstrangeira')
    ])
    listas.tiposHedge = tiposHedge
    listas.instituicoes = resInst.data
    listas.statusHedge = statusHedge
    listas.moedas = moedas
  } catch (e) { console.warn('Erro ao carregar listas:', e) }
}

const formatarMoeda = (valor) => {
  if (valor == null) return '-'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)
}

const getItemId = (item) => item?.idListaSistemaItem || item?.idListaClienteItem

const getTextoMoeda = (idMoeda) => {
  if (!idMoeda) return '-'
  const idStr = String(idMoeda)
  const moeda = listas.moedas.find(m =>
    String(m.idListaSistemaItem) === idStr || String(m.idListaClienteItem) === idStr
  )
  return moeda ? moeda.textoLista : idMoeda
}

const getTextoStatus = (idStatus) => {
  if (!idStatus) return '-'
  const idStr = String(idStatus)
  const status = listas.statusHedge.find(s =>
    String(s.idListaSistemaItem) === idStr || String(s.idListaClienteItem) === idStr
  )
  return status ? status.textoLista : idStatus
}

const obterCorStatus = (lstStatus) => {
  if (lstStatus?.includes('ABERTO')) return 'badge-ghost-warning'
  if (lstStatus?.includes('LIQUIDADO')) return 'badge-ghost-success'
  if (lstStatus?.includes('CANCELADO')) return 'badge-ghost-danger'
  if (lstStatus?.includes('VENCIDO')) return 'badge-ghost-secondary'
  return 'badge-ghost-primary'
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.buscaLivre) params.append('buscaLivre', filtros.buscaLivre)
    if (filtros.lstTipoHedge) params.append('lstTipoHedge', filtros.lstTipoHedge)
    if (filtros.lstStatusHedge) params.append('lstStatusHedge', filtros.lstStatusHedge)
    if (filtros.lstMoedaEstrangeira) params.append('lstMoedaEstrangeira', filtros.lstMoedaEstrangeira)
    if (filtros.idInstituicaoFinanceira) params.append('idInstituicaoFinanceira', filtros.idInstituicaoFinanceira)
    if (filtros.dataFechamentoInicio) params.append('dataFechamentoInicio', filtros.dataFechamentoInicio)
    if (filtros.dataFechamentoFim) params.append('dataFechamentoFim', filtros.dataFechamentoFim)
    if (filtros.dataVencimentoInicio) params.append('dataVencimentoInicio', filtros.dataVencimentoInicio)
    if (filtros.dataVencimentoFim) params.append('dataVencimentoFim', filtros.dataVencimentoFim)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/OperacaoHedge?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar os dados.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }

const limparFiltros = () => {
  filtros.buscaLivre = ''
  filtros.lstTipoHedge = ''
  filtros.lstStatusHedge = ''
  filtros.lstMoedaEstrangeira = ''
  filtros.idInstituicaoFinanceira = ''
  filtros.dataFechamentoInicio = ''
  filtros.dataFechamentoFim = ''
  filtros.dataVencimentoInicio = ''
  filtros.dataVencimentoFim = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarItem = (item) => { emit('editar-item', item) }

const abrirLiquidar = () => {
  emit('show-notification', { message: 'Funcionalidade em desenvolvimento.', type: 'info' })
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idOperacaoHedge
  auditoriaSelecionada.nome = item.numeroContrato
  mostrarAuditoria.value = true
}

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
  }
})

onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Contratos de Hedge (Derivativos)"
    subtitulo="Gestão de proteção cambial, NDFs e Swaps para operações de comércio exterior"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Busca Livre (Contrato / Obs)</label>
          <input type="text" class="form-control" v-model="filtros.buscaLivre" placeholder="Nº contrato, observação..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Tipo de Hedge</label>
          <select class="form-select" v-model="filtros.lstTipoHedge" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="t in listas.tiposHedge" :key="getItemId(t)" :value="getItemId(t)">{{ t.textoLista }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatusHedge" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in listas.statusHedge" :key="getItemId(s)" :value="getItemId(s)">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <CampoBuscaLista id="lstMoedaEstrangeira" label="Moeda" tabela="OperacaoHedge" v-model="filtros.lstMoedaEstrangeira" />
        </div>
        <div class="col-md-4">
          <label class="form-label">Instituição Financeira</label>
          <select class="form-select" v-model="filtros.idInstituicaoFinanceira" @change="aplicarFiltros">
            <option value="">Todos os Bancos</option>
            <option v-for="b in listas.instituicoes" :key="b.idParceiro" :value="b.idParceiro">{{ b.nomeFantasia || b.razaoSocial }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <CampoDataIntervalo id="filtroFechamento" label="Período de Fechamento" v-model:modelValueInicio="filtros.dataFechamentoInicio" v-model:modelValueFim="filtros.dataFechamentoFim" />
        </div>
        <div class="col-md-3">
          <CampoDataIntervalo id="filtroVencimento" label="Período de Vencimento" v-model:modelValueInicio="filtros.dataVencimentoInicio" v-model:modelValueFim="filtros.dataVencimentoFim" />
        </div>
        <div class="col-md-3 ms-auto text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum contrato de hedge encontrado."
      row-key="idOperacaoHedge"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarItem($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idOperacaoHedge }}</span>
      </template>

      <template #tipo="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ row.lstTipoHedge }}
        </span>
      </template>

      <template #posicao="{ row }">
        <span :class="row.lstTipoPosicao === 'COMPRA' ? 'text-primary' : 'text-danger'">
          {{ row.lstTipoPosicao }}
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterCorStatus(row.lstStatusHedge)">
          {{ getTextoStatus(row.lstStatusHedge) }}
        </span>
      </template>

      <template #fechamento="{ row }">
        {{ formatarDataBR(row.dataFechamento) }}
      </template>

      <template #vencimento="{ row }">
        {{ formatarDataBR(row.dataVencimento) }}
      </template>

      <template #moeda="{ row }">
        <span class="text-body-secondary">
          {{ getTextoMoeda(row.lstMoedaEstrangeira) }}
        </span>
      </template>

      <template #valorNotional="{ row }">
        {{ formatarMoeda(row.valorMoedaEstrangeira, row.lstMoedaEstrangeira) }}
      </template>

      <template #taxa="{ row }">
        <span class="fw-medium">{{ row.taxaContratada?.toFixed(4) }}</span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button v-if="temPermissaoCompleta" class="btn btn-sm btn-outline-success border-0 py-0" @click="abrirLiquidar(row)" title="Liquidar Contrato">
            <i class="bi bi-currency-exchange me-1"></i> Liquidar
          </button>
          <div class="vr mx-1" v-if="temPermissaoCompleta"></div>
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarItem(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="OperacaoHedge"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
</style>
